import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import css from './ListingPage.module.css';
import ReactCountryFlag from 'react-country-flag';
import { TUTOR } from '../../util/types.js';
import {
  arabicCountriesList,
  getAvailableTeachingLanguages,
  getExperienceParts,
  getYearsFromListing,
} from '../../util/constants.js';
import getCountryCodes from '../../translations/countryCodes';
import { StudentRewardsSummary } from '../../components';
import { cleanPlaceholderText } from '../../util/data.js';

const AuthorHeader = ({
  avatarUrl,
  authorDisplayName,
  shortTitle,
  spokenLanguage,
  teachingLanguages,
  languagePreference,
  bio,
  country,
  userType,
  author,
  professionalBadge,
  dialectCountry,
  countryOfOrigin,
  listing,
}) => {
  const intl = useIntl();
  const language = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';

  const countryIso2 = (country || '').trim().toUpperCase();
  const countryOfOriginIso2 =
    (countryOfOrigin || '').trim().length === 2 ? (countryOfOrigin || '').trim().toUpperCase() : '';

  const countryCodes = useMemo(() => getCountryCodes(intl.locale), [intl.locale]);

  const originCountryName = useMemo(() => {
    const code = countryOfOriginIso2;
    if (!code) return '';
    const match = countryCodes.find(c => c.code === code);
    return match ? match.name : code;
  }, [countryCodes, countryOfOriginIso2]);

  const residenceCountryName = useMemo(() => {
    const code = countryIso2;
    if (!code) return '';
    const match = countryCodes.find(c => c.code === code);
    return match ? match.name : code;
  }, [countryCodes, countryIso2]);

  const originTooltip = intl.formatMessage(
    { id: 'ListingCard.originCountryTooltip', defaultMessage: 'Originally from {country}' },
    { country: originCountryName }
  );
  const teachingTooltip = intl.formatMessage(
    { id: 'ListingCard.teachingCountryTooltip', defaultMessage: 'Currently teaching from {country}' },
    { country: residenceCountryName }
  );

  const hasMultipleFlags = countryOfOriginIso2 && countryIso2 && countryOfOriginIso2 !== countryIso2;

  const fallbackBio = intl.formatMessage({
    id: 'ListingPage.aboutMeFallback',
    defaultMessage: 'Bio hasn’t been added yet.',
  });
  const languagesStr = (spokenLanguage || [])
    .map(l => (language === 'en' ? l?.label : l?.label_ar))
    .filter(Boolean)
    .join(', ');
  const fallbackSpeaks = intl.formatMessage({
    id: 'ListingPage.speaksFallback',
    defaultMessage: 'No languages specified yet.',
  });
  const { isActiveTutor = false, isFastResponder = false, isReliableTutor = false } = author?.attributes?.profile?.publicData || {};

  // Compute tutor's earned tier strictly from Section 2.1 price bands framework
  const listingPublicData = listing?.attributes?.publicData || {};
  const authorPublicData =
    author?.attributes?.profile?.publicData ||
    author?.attributes?.publicData ||
    {};
  const isCertified =
    listingPublicData.priceBand === 'arabibi_certified' ||
    authorPublicData.priceBand === 'arabibi_certified' ||
    listingPublicData.adminPriceBand === 'arabibi_certified' ||
    authorPublicData.adminPriceBand === 'arabibi_certified';

  const experienceYears = getYearsFromListing(listing, author);
  const experienceParts = useMemo(() => {
    return getExperienceParts(experienceYears, intl, { isCertified });
  }, [experienceYears, intl, isCertified]);

  const rawTeachingLanguages = useMemo(() => {
    // 1. Direct prop if non-empty
    if (Array.isArray(teachingLanguages) && teachingLanguages.length > 0) {
      return teachingLanguages;
    }
    if (typeof teachingLanguages === 'string' && teachingLanguages.trim()) {
      return [teachingLanguages.trim()];
    }

    // 2. Listing publicData
    const listingPublic = listing?.attributes?.publicData || {};
    if (Array.isArray(listingPublic.teachingLanguages) && listingPublic.teachingLanguages.length > 0) {
      return listingPublic.teachingLanguages;
    }
    if (typeof listingPublic.teachingLanguages === 'string' && listingPublic.teachingLanguages.trim()) {
      return [listingPublic.teachingLanguages.trim()];
    }

    // 3. Author publicData
    const authorPublic =
      author?.attributes?.profile?.publicData ||
      author?.attributes?.publicData ||
      {};
    if (Array.isArray(authorPublic.teachingLanguages) && authorPublic.teachingLanguages.length > 0) {
      return authorPublic.teachingLanguages;
    }
    if (typeof authorPublic.teachingLanguages === 'string' && authorPublic.teachingLanguages.trim()) {
      return [authorPublic.teachingLanguages.trim()];
    }

    // 4. Single teachingLanguage or languagePreference
    const single =
      listingPublic.teachingLanguage ||
      authorPublic.teachingLanguage ||
      listingPublic.languagePreference ||
      authorPublic.languagePreference ||
      languagePreference;
    if (single) {
      const s = String(single).toLowerCase().trim();
      if (s === 'ar' || s === 'arabic') return ['arabic'];
      if (s === 'en' || s === 'english') return ['english'];
      if (s === 'fr' || s === 'french') return ['french'];
      if (s === 'es' || s === 'spanish') return ['spanish'];
      if (s === 'de' || s === 'german') return ['german'];
      if (s === 'tr' || s === 'turkish') return ['turkish'];
      if (s === 'ur' || s === 'urdu') return ['urdu'];
    }

    // 5. Derive from spoken languages (Arabic, English, French among spoken languages)
    const available = getAvailableTeachingLanguages(spokenLanguage);
    if (Array.isArray(available) && available.length > 0) {
      return available.map(opt => opt.value);
    }

    // 6. Default fallback for Arabibi platform
    return ['arabic'];
  }, [teachingLanguages, listing, author, languagePreference, spokenLanguage]);

  const teachingLanguagesStr = useMemo(() => {
    return (Array.isArray(rawTeachingLanguages) ? rawTeachingLanguages : [rawTeachingLanguages])
      .map(item => {
        const key = (typeof item === 'string' ? item : item?.key || item?.value || item?.option || '').toLowerCase().trim();
        if (key === 'arabic' || key === 'ar') return language === 'ar' ? 'العربية' : 'Arabic';
        if (key === 'english' || key === 'en') return language === 'ar' ? 'الإنجليزية' : 'English';
        if (key === 'french' || key === 'fr') return language === 'ar' ? 'الفرنسية' : 'French';
        if (key === 'spanish' || key === 'es') return language === 'ar' ? 'الإسبانية' : 'Spanish';
        if (key === 'german' || key === 'de') return language === 'ar' ? 'الألمانية' : 'German';
        if (key === 'turkish' || key === 'tr') return language === 'ar' ? 'التركية' : 'Turkish';
        if (key === 'urdu' || key === 'ur') return language === 'ar' ? 'الأوردية' : 'Urdu';
        return typeof item === 'object' ? (language === 'ar' ? item?.label_ar : item?.label) : item;
      })
      .filter(Boolean)
      .join(', ');
  }, [language, rawTeachingLanguages]);

  // Check teachFromZero capability or arabic_from_scratch specialization (Item 2.9)
  const specializations = listingPublicData.specializations || authorPublicData.specializations || [];
  const teachFromZero =
    listingPublicData.teachFromZero === 'true' ||
    listingPublicData.teachFromZero === true ||
    listingPublicData.teachFromZero === 'yes' ||
    authorPublicData.teachFromZero === 'true' ||
    authorPublicData.teachFromZero === true ||
    authorPublicData.teachFromZero === 'yes' ||
    (Array.isArray(specializations) && specializations.includes('arabic_from_scratch'));

  const displayLanguages = languagesStr || fallbackSpeaks;

  return (
    <div className={css.listingProfileWrapper}>
      <div className={css.authorHeader}>
        <div className={css.authorAvatarWrapper}>
          {avatarUrl ? (
            <img
              className={css.authorAvatar}
              src={avatarUrl}
              alt={authorDisplayName}
              loading="lazy"
            />
          ) : (
            <div className={css.noImage}>
              <IconCard type="blankimage" />
            </div>
          )}
        </div>
        <div className={css.authorMeta}>
          <div className={css.authorName}>
            {authorDisplayName}
            {hasMultipleFlags ? (
              <span className={css.flagGroup}>
                <ReactCountryFlag
                  countryCode={countryOfOriginIso2}
                  svg
                  className={css.flagPrimary}
                  title={originTooltip}
                  aria-label={originTooltip}
                />
                <ReactCountryFlag
                  countryCode={countryIso2}
                  svg
                  className={css.flagSecondary}
                  title={teachingTooltip}
                  aria-label={teachingTooltip}
                />
              </span>
            ) : countryOfOriginIso2 ? (
              <span className={css.flagGroup}>
                <ReactCountryFlag
                  countryCode={countryOfOriginIso2}
                  svg
                  className={css.flagPrimary}
                  title={originTooltip}
                  aria-label={originTooltip}
                />
              </span>
            ) : countryIso2 ? (
              <span className={css.flagGroup}>
                <ReactCountryFlag
                  countryCode={countryIso2}
                  svg
                  className={css.flagPrimary}
                  title={teachingTooltip}
                  aria-label={teachingTooltip}
                />
              </span>
            ) : null}
          </div>

          {/* Sanitize placeholder text from tutor profile header (Item 2.2) */}
          {(() => {
            const cleanedShortTitle = cleanPlaceholderText(shortTitle);
            return cleanedShortTitle ? <div className={css.authorShortTitle}>{cleanedShortTitle}</div> : null;
          })()}

          {teachFromZero && (
            <div className={css.scratchBadgeRow}>
              <span className={css.scratchBadge}>
                <FormattedMessage id="ListingCard.badge.teachesFromScratch" defaultMessage="Teaches from Scratch" />
              </span>
            </div>
          )}

          {!(userType === TUTOR || userType === 'tutor' || author?.attributes?.profile?.publicData?.userType === TUTOR || author?.attributes?.profile?.publicData?.userType === 'tutor') ? (
            <StudentRewardsSummary studentUserId={author?.id?.uuid || (typeof author?.id === 'string' ? author.id : null)} />
          ) : null}

          {/* Speaks & Dialect Info Row */}
          <div className={css.authorInfoRow}>
            {teachingLanguagesStr ? (
              <div className={css.authorInfoItem}>
                <IconCard type="speaks" />
                <span>
                  <FormattedMessage id="ListingPage.teachesInLabel" defaultMessage="Teaches in" />: {teachingLanguagesStr}
                </span>
              </div>
            ) : null}
            <div className={css.authorInfoItem}>
              <IconCard type="speaks" />
              <span>
                <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />: {displayLanguages}
              </span>
            </div>
            {(() => {
              const activeDialects =
                Array.isArray(listing?.attributes?.publicData?.dialects) && listing.attributes.publicData.dialects.length > 0
                  ? listing.attributes.publicData.dialects
                  : Array.isArray(author?.attributes?.profile?.publicData?.dialects) && author.attributes.profile.publicData.dialects.length > 0
                  ? author.attributes.profile.publicData.dialects
                  : dialectCountry
                  ? [dialectCountry]
                  : [];

              if (!activeDialects || activeDialects.length === 0) return null;

              const dialectElements = activeDialects.map(d => {
                const code = (typeof d === 'string' ? d : d?.value || d?.key || '').toUpperCase();
                const countryItem = arabicCountriesList.find(c => c.value === code || c.key === code);
                const label = countryItem ? (language === 'ar' ? countryItem.label_ar : countryItem.label_en) : code;
                return (
                  <span key={code}>
                    {countryItem && (
                      <ReactCountryFlag
                        countryCode={code}
                        svg
                        className={css.dialectFlag}
                        title={label}
                      />
                    )}{' '}
                    {label}
                  </span>
                );
              });

              return (
                <div className={css.authorInfoItem}>
                  <IconCard type="speaks" />
                  <span>
                    <FormattedMessage id="ListingPage.dialectCountryLabel" defaultMessage="Dialect" />:{' '}
                    {dialectElements.reduce((acc, el, idx) => (idx === 0 ? [el] : [...acc, ', ', el]), [])}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Response Time Row */}
          {(userType === TUTOR || userType === 'tutor') ? (
            <div className={css.responseTextRow}>
              <span className={css.responseLabel}>
                <FormattedMessage id="ListingPage.response.heading" defaultMessage="Response Time" />:{' '}
              </span>
              <span className={css.responseValue}>
                {isActiveTutor ? (
                  <FormattedMessage id="ListingPage.response.status.activeTutor" defaultMessage="Active Tutor" />
                ) : isFastResponder ? (
                  <FormattedMessage id="ListingPage.response.status.fastResponder" defaultMessage="Fast Responder" />
                ) : isReliableTutor ? (
                  <FormattedMessage id="ListingPage.response.status.reliableTutor" defaultMessage="Reliable Tutor" />
                ) : (
                  <FormattedMessage id="ListingPage.response.status.oftenActive" defaultMessage="Often Active" />
                )}
                {' ⭐'}
              </span>
            </div>
          ) : null}

          <hr className={css.divider} />

          {/* Tutor Tier & Credentials Section (aligned with Section 2.1 bands) */}
          {(userType === TUTOR || userType === 'tutor') && (professionalBadge || experienceParts.tier) && (
            <div className={css.badgeSection}>
              <div className={css.badgeCard}>
                <div className={css.badgeIcon}>
                  <IconCard type="professional" />
                </div>
                <div className={css.badgeText}>
                  <div className={css.badgeTitle}>
                    {experienceParts.tier || (
                      <FormattedMessage id="ListingPage.badge.title" defaultMessage="Certified Tutor" />
                    )}
                  </div>
                  <div className={css.badgeSubtitle}>
                    {experienceParts.yearsText ? (
                      experienceParts.yearsText
                    ) : (
                      <FormattedMessage
                        id="ListingPage.badge.subtitle"
                        defaultMessage="{name} is a qualified tutor"
                        values={{ name: authorDisplayName }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className={css.verticalDivider} />
              <div className={css.badgeCard}>
                <div className={css.badgeIcon}>
                  <IconCard type="tutor" />
                </div>
                <div className={css.badgeText}>
                  <div className={css.badgeTitle}>
                    <FormattedMessage id="ListingPage.teachesLabel" defaultMessage="Teaches" />
                  </div>
                  <div className={css.badgeSubtitle}>
                    <FormattedMessage id="ListingPage.language.arabicLessons" defaultMessage="Arabic Lessons" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {(() => {
        const cleanedBio = cleanPlaceholderText(bio);
        return cleanedBio ? <AuthorBioSection bio={cleanedBio} /> : <AuthorBioSection bio={fallbackBio} />;
      })()}
    </div>
  );
};

export default AuthorHeader;

const AuthorBioSection = ({ bio }) => {
  const [expanded, setExpanded] = useState(false);
  const { text, isLong } = useMemo(() => {
    if (typeof bio !== 'string') return { text: '', isLong: false };
    const trimmed = bio.trim();
    if (trimmed.length > 300) {
      return { text: expanded ? trimmed : trimmed.slice(0, 300) + '...', isLong: true };
    }
    return { text: trimmed, isLong: false };
  }, [bio, expanded]);

  return (
    <div className={css.authorBio}>
      <div className={css.authorBioHeading}>
        <IconCard type="about-me" />
        <FormattedMessage id="ListingPage.aboutMeHeading" />
      </div>
      <div className={css.listingDescription}>
        {text}
        {isLong ? (
          <button type="button" onClick={() => setExpanded(e => !e)} className={css.seeMoreLess}>
            {expanded ? (
              <FormattedMessage id="ListingPage.authorBio.showLess" />
            ) : (
              <FormattedMessage id="ListingPage.authorBio.showMore" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};
